import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider } from "@mui/material";
import { Stack } from "components/layout/reusableLayouts";
import {
  COMPLETE_BANK_LINK,
  REMOVE_HOSTED_BANK_ACCOUNT,
} from "../../../graphql/company";
import { useMutation } from "@apollo/client";
import DocumentViewerDialog from "../../common/DocumentViewerDialog";
import BankLink from "./BankLink";
import RewardsBankLink from "../rewards/RewardsBankLink";
import { HugeiconsIcon } from "@hugeicons/react";
import { Add01Icon, BankIcon } from "@hugeicons-pro/core-stroke-rounded";
import AccountType from "enums/AccountType";
import AccountStatus from "enums/AccountStatus";
import { BankAccount, CardProgramSummary } from "hooks/useGetBankAccounts";
import { Chip, ConfirmDialog, DataRow, Drawer, toast } from "@outpave/ds";

interface ConnectedFundingAccountDrawerProps {
  open: boolean;
  handleCloseDrawer: () => void;
  bankAccountsData: BankAccount[];
  refetchAccounts: () => void;
  optionalTitle?: string;
  accountType?: AccountType;
  loadingBankAccountData?: boolean;
  /** The program a new bank is linked for, which decides how it is linked. */
  program?: CardProgramSummary;
  rewards?: boolean;
}

const ConnectedFundingAccountDrawer: React.FC<
  ConnectedFundingAccountDrawerProps
> = (props) => {
  const {
    open,
    handleCloseDrawer,
    bankAccountsData,
    refetchAccounts,
    optionalTitle,
    accountType = AccountType.BANK,
    loadingBankAccountData = false,
    program,
    rewards = false,
  } = props;
  const [openApplyPresetModal, setOpenApplyPresetModal] =
    useState<boolean>(false);
  const [modalData, setModalData] = useState<string>();
  const [popoverUrl, setPopoverUrl] = useState<string>("");
  const [openPopover, setOpenPopover] = useState<boolean>(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [bankLinkActive, setBankLinkActive] = useState(false);
  // Closing verifies the account, so it must save once and only once. The
  // viewer no longer double-fires its close (the old Popover ran both a Modal
  // onClose and its own click-outside listener), but this still guards
  // re-entry: the panel stays open across the await, so a second dismissal can
  // arrive before `savingHostedBankAccount` has flipped.
  const closeOnceRef = useRef(false);

  const { t } = useTranslation();

  const [saveHostedBankAccount, { loading: savingHostedBankAccount }] =
    useMutation(COMPLETE_BANK_LINK);

  const [removeAccount, { loading: loadingRemoval }] = useMutation(
    REMOVE_HOSTED_BANK_ACCOUNT,
    {
      onCompleted: () => {
        toast.success(`Account removed!`);
        refetchAccounts();
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  );

  const handleApplyPresetModal = (id: string) => {
    setModalData(id);
    setOpenApplyPresetModal(true);
  };

  const handleOpenPopover = (url?: string) => {
    if (!url) return;
    setPopoverUrl(url);
    setOpenPopover(true);
  };

  const handleClosePopover = async () => {
    let shouldSave = true;

    try {
      if (closeOnceRef.current) shouldSave = false;
      if (savingHostedBankAccount) shouldSave = false;
      if (!accountId)
        throw new Error(t("SomethingWentWrongDescriptionWithSupport"));

      closeOnceRef.current = true;

      if (shouldSave) {
        await saveHostedBankAccount({
          variables: {
            input: {
              accountId,
              type:
                bankAccountsData.find((account) => account.id === accountId)
                  ?.accountType ?? accountType,
            },
          },
        });
        toast.success(t("Account successfully verified"));
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      refetchAccounts();
      setPopoverUrl("");
      setOpenPopover(false);
      closeOnceRef.current = false;
    }
  };

  const handleRemoveBank = (accountId: string) => {
    removeAccount({
      variables: {
        accountId: accountId,
      },
    });
    setModalData(undefined);
  };

  // Gates the overlay AND every dismissal path, so the two can't drift.
  // Includes the hosted-verification save: it runs after the popover closes,
  // with nothing else covering the drawer.
  const busy =
    loadingRemoval || loadingBankAccountData || savingHostedBankAccount;

  const showAddNewAccount =
    accountType !== AccountType.PERSONAL_BANK ||
    !bankAccountsData ||
    bankAccountsData.length === 0;

  const renderAddTrigger = (handleOpenAddBank: () => void) => (
    <Button
      variant="primary-soft"
      size="small"
      onClick={handleOpenAddBank}
      startIcon={<HugeiconsIcon icon={Add01Icon} size={16} />}
      sx={{ alignSelf: "flex-start", marginTop: "var(--space-4)" }}
    >
      {t("Add New Account")}
    </Button>
  );

  return (
    <Drawer
      open={open}
      disableEnforceFocus={bankLinkActive}
      onClose={() => {
        if (busy) return;
        handleCloseDrawer();
      }}
      title={optionalTitle ? [optionalTitle] : [t("Connected Accounts")]}
      loading={busy}
      footer={
        <Button
          variant="default-outlined"
          disabled={busy}
          onClick={handleCloseDrawer}
        >
          {t("Exit")}
        </Button>
      }
    >
      <Stack gap="0">
        {bankAccountsData?.length > 0 &&
          bankAccountsData.map((account: BankAccount) => {
            const pendingDeposits =
              account.status === AccountStatus.MICRODEPOSITS_PENDING;

            return (
              <React.Fragment key={account.id}>
                <DataRow
                  type="amount"
                  icon={{
                    glyph: <HugeiconsIcon icon={BankIcon} size={18} />,
                    tone: "brand",
                  }}
                  title={account.bankName}
                  subtitle={account.last4 ? `•••• ${account.last4}` : undefined}
                  tag={
                    pendingDeposits ? (
                      <Chip
                        size="sm"
                        tone="warning"
                        label={t("Pending verification")}
                      />
                    ) : undefined
                  }
                  trailing={
                    pendingDeposits ? (
                      <Button
                        size="small"
                        variant="default-outlined"
                        onClick={() => {
                          setAccountId(account.id);
                          handleOpenPopover(account.microDepositUrl);
                        }}
                      >
                        {t("Enter Deposits")}
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="danger-outlined"
                        onClick={() => {
                          handleApplyPresetModal(account.id);
                        }}
                      >
                        {t("Remove")}
                      </Button>
                    )
                  }
                />
                <Divider />
              </React.Fragment>
            );
          })}
        {showAddNewAccount &&
          (rewards ? (
            <RewardsBankLink
              onActiveChange={setBankLinkActive}
              accountType={accountType}
              refetchBankAccounts={refetchAccounts}
              renderTrigger={renderAddTrigger}
            />
          ) : (
            <BankLink
              program={program}
              onActiveChange={setBankLinkActive}
              accountType={accountType}
              refetchBankAccounts={refetchAccounts}
              renderTrigger={renderAddTrigger}
            />
          ))}
      </Stack>

      <ConfirmDialog
        open={openApplyPresetModal}
        title={t("Remove Account")}
        confirmLabel={t("Remove")}
        cancelLabel={t("Cancel")}
        onConfirm={() => {
          if (modalData) {
            handleRemoveBank(modalData);
          }
          setOpenApplyPresetModal(false);
        }}
        onCancel={() => {
          setOpenApplyPresetModal(false);
        }}
        message={t("Are you sure you want to remove this account?")}
      />
      <DocumentViewerDialog
        url={popoverUrl}
        open={openPopover}
        title={t("Bank verification")}
        onClose={handleClosePopover}
      />
    </Drawer>
  );
};

export default ConnectedFundingAccountDrawer;
